/* eslint-disable */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Button,
  Input,
  Tag,
  Modal,
  Alert,
  Dropdown,
  Spin,
  Space,
  DatePicker,
  Select,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { promoCodesApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";
import dayjs from "dayjs";

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PromoCodesList = () => {
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const [filters, setFilters] = useState({ page: 1, limit: 10, prefix: "" });
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    id: null,
    code: "",
  });
  const [deactivateModal, setDeactivateModal] = useState({
    visible: false,
    id: null,
    code: "",
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);
        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions
  const canViewPromoCodes = useMemo(() => {
    return hasPermission(userPermissions, "promocodes.view");
  }, [userPermissions]);

  const canCreatePromoCodes = useMemo(() => {
    return hasPermission(userPermissions, "promocodes.create");
  }, [userPermissions]);

  const canEditPromoCodes = useMemo(() => {
    return hasPermission(userPermissions, "promocodes.edit");
  }, [userPermissions]);

  const canDeletePromoCodes = useMemo(() => {
    return hasPermission(userPermissions, "promocodes.delete");
  }, [userPermissions]);

  const canManagePromoCodes = useMemo(() => {
    return canCreatePromoCodes || canEditPromoCodes || canDeletePromoCodes;
  }, [canCreatePromoCodes, canEditPromoCodes, canDeletePromoCodes]);

  useEffect(() => {
    if (!isCheckingPermission && canViewPromoCodes) {
      fetchPromoCodes();
    }
  }, [filters, debouncedSearch, canViewPromoCodes, isCheckingPermission]);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const query = {
        page: filters.page,
        limit: filters.limit,
        ...(filters.prefix ? { prefix: filters.prefix } : {}),
      };

      const response = await promoCodesApi.getAll(query);
      setPromoCodes(response.data || []);
      setTotalItems(response.total || 0);
      setSearching(false);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      if (error.response?.status === 403) {
        message.error("You don't have permission to view promo codes");
      } else {
        message.error(
          error.response?.data?.message || "Failed to load promo codes",
        );
      }
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setFilters((f) => ({ ...f, prefix: value, page: 1 }));
      setSearching(true);
    }, 300);
  }, []);

  const handleDeactivate = async () => {
    if (!canEditPromoCodes) {
      message.error("You don't have permission to deactivate promo codes");
      setDeactivateModal({ visible: false, id: null, code: "" });
      return;
    }

    try {
      setDeactivateLoading(true);
      await promoCodesApi.deactivate(deactivateModal.id);
      message.success("Promo code deactivated successfully");
      setDeactivateModal({ visible: false, id: null, code: "" });
      fetchPromoCodes();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to deactivate promo codes");
      } else {
        message.error(
          e.response?.data?.message || "Failed to deactivate promo code",
        );
      }
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canDeletePromoCodes) {
      message.error("You don't have permission to delete promo codes");
      setDeleteModal({ visible: false, id: null, code: "" });
      return;
    }

    try {
      setDeleteLoading(true);
      await promoCodesApi.remove(deleteModal.id);
      message.success("Promo code deleted successfully");
      setDeleteModal({ visible: false, id: null, code: "" });
      fetchPromoCodes();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete promo codes");
      } else {
        message.error(
          e.response?.data?.message || "Failed to delete promo code",
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusTag = (record) => {
    const today = dayjs().format("YYYY-MM-DD");
    const isValidDate = record.validFrom <= today && today <= record.validUntil;
    const isWithinUsage = record.usedCount < record.maxUses;
    const isActive = record.isActive && isValidDate && isWithinUsage;

    if (!record.isActive) {
      return <Tag color="default">Inactive</Tag>;
    }
    if (!isValidDate) {
      return <Tag color="orange">Expired</Tag>;
    }
    if (!isWithinUsage) {
      return <Tag color="orange">Used Up</Tag>;
    }
    return <Tag color="green">Active</Tag>;
  };

  const getUsageStatus = (record) => {
    const percentage = (record.usedCount / record.maxUses) * 100;
    let color = "#52c41a";
    if (percentage >= 90) color = "#f5222d";
    else if (percentage >= 70) color = "#faad14";

    return (
      <div>
        <div style={{ marginBottom: 4 }}>
          {record.usedCount} / {record.maxUses}
        </div>
        <div
          style={{
            width: "100%",
            height: 4,
            backgroundColor: "#f0f0f0",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              height: "100%",
              backgroundColor: color,
              transition: "width 0.3s",
            }}
          />
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Promo Code",
      dataIndex: "code",
      key: "code",
      render: (code, record) => (
        <div>
          <strong style={{ fontFamily: "monospace", fontSize: 14 }}>
            {code}
          </strong>
          <div className="small text-muted">Prefix: {record.prefix}</div>
        </div>
      ),
    },
    {
      title: "Discount",
      dataIndex: "discountPercentage",
      key: "discountPercentage",
      render: (value) => (
        <Tag color="blue" style={{ fontSize: 14 }}>
          {value}% OFF
        </Tag>
      ),
      sorter: (a, b) => a.discountPercentage - b.discountPercentage,
    },
    {
      title: "Valid Period",
      key: "validPeriod",
      render: (_, record) => (
        <div>
          <div>From: {dayjs(record.validFrom).format("MMM DD, YYYY")}</div>
          <div>To: {dayjs(record.validUntil).format("MMM DD, YYYY")}</div>
        </div>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, record) => getUsageStatus(record),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("MMM DD, YYYY"),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    ...(canManagePromoCodes
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => {
              const items = [];

              // if (canViewPromoCodes) {
              //   items.push({
              //     key: "view",
              //     label: "View Details",
              //     icon: <EyeOutlined />,
              //     onClick: () => history.push(`/promo-codes/${record.id}`),
              //   });
              // }

              if (canEditPromoCodes && record.isActive) {
                const today = dayjs().format("YYYY-MM-DD");
                const isValid =
                  record.validFrom <= today && today <= record.validUntil;
                if (isValid && record.usedCount < record.maxUses) {
                  items.push({
                    key: "deactivate",
                    label: "Deactivate",
                    icon: <StopOutlined />,
                    onClick: () =>
                      setDeactivateModal({
                        visible: true,
                        id: record.id,
                        code: record.code,
                      }),
                  });
                }
              }

              if (canDeletePromoCodes) {
                if (
                  items.length > 0 &&
                  items[items.length - 1].key !== "divider"
                ) {
                  items.push({ type: "divider" });
                }
                items.push({
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  onClick: () =>
                    setDeleteModal({
                      visible: true,
                      id: record.id,
                      code: record.code,
                    }),
                  danger: true,
                });
              }

              if (items.length === 0) return null;

              return (
                <Dropdown
                  menu={{ items }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
              );
            },
          },
        ]
      : []),
  ];

  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewPromoCodes) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view promo codes.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> promocodes.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5>Promo Code Management</h5>
                    <p className="text-muted mb-0 mt-1">
                      {canManagePromoCodes
                        ? `You have ${canCreatePromoCodes ? "create, " : ""}${
                            canEditPromoCodes ? "edit, " : ""
                          }${
                            canDeletePromoCodes ? "delete" : ""
                          } access to manage promo codes`
                        : "You can view promo codes but cannot modify them"}
                    </p>
                  </div>
                  {canCreatePromoCodes && (
                    <div className="list-btn">
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: "#7B4D12",
                          borderColor: "#7B4D12",
                        }}
                        icon={<PlusOutlined />}
                        onClick={() => history.push("/promo-codes/generate")}
                      >
                        Generate Promo Codes
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {!canManagePromoCodes && canViewPromoCodes && (
                <Alert
                  message="View Only Mode"
                  description="You have view-only access to promo codes. To generate, edit, or delete promo codes, please contact an administrator."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              <div className="row mb-4">
                <div className="col-md-4">
                  <Search
                    placeholder={
                      searching ? "Searching..." : "Search by prefix..."
                    }
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                    loading={searching}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {promoCodes.length === 0 && !loading ? (
                <div className="text-center py-5">
                  <p className="text-muted">No promo codes found</p>
                  {canCreatePromoCodes && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/promo-codes/generate")}
                    >
                      Generate your first promo codes
                    </Button>
                  )}
                </div>
              ) : (
                <ResponsiveTable
                  columns={columns}
                  dataSource={promoCodes}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total: totalItems,
                    showSizeChanger: true,
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Confirmation Modal */}
      {canEditPromoCodes && (
        <Modal
          title={
            <span>
              <StopOutlined style={{ marginRight: 8, color: "#faad14" }} />
              Deactivate Promo Code
            </span>
          }
          open={deactivateModal.visible}
          onOk={handleDeactivate}
          onCancel={() =>
            setDeactivateModal({ visible: false, id: null, code: "" })
          }
          confirmLoading={deactivateLoading}
          okText="Yes, Deactivate"
          okType="danger"
          width={420}
        >
          <Alert
            message="This promo code will no longer be usable for new bookings."
            type="warning"
            showIcon
            className="mb-3"
          />
          <p>
            Are you sure you want to deactivate{" "}
            <strong>{deactivateModal.code}</strong>?
          </p>
          <Alert
            message="Deactivated codes can still be viewed but cannot be applied to new reservations."
            type="info"
            showIcon
            className="mt-2"
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {canDeletePromoCodes && (
        <Modal
          title={
            <span>
              <DeleteOutlined style={{ marginRight: 8, color: "#dc2626" }} />
              Delete Promo Code
            </span>
          }
          open={deleteModal.visible}
          onOk={handleDelete}
          onCancel={() =>
            setDeleteModal({ visible: false, id: null, code: "" })
          }
          confirmLoading={deleteLoading}
          okText="Yes, Delete"
          okType="danger"
          width={420}
        >
          <Alert
            message="This promo code will be permanently removed from the system."
            type="warning"
            showIcon
            className="mb-3"
          />
          <p>
            Are you sure you want to delete <strong>{deleteModal.code}</strong>?
          </p>
          <Alert
            message="This action cannot be undone. Any reservations that used this code will not be affected."
            type="error"
            showIcon
            className="mt-2"
          />
        </Modal>
      )}
    </div>
  );
};

export default PromoCodesList;
